<template>
  <v-row>
    <v-col cols="12">
      <v-row v-if="props.showDocumentList" class="section-container">
        <v-col cols="12">
          <h3 class="text-primary weight-600 mb-1">Documents</h3>
          <OrderDocumentList
            :patient-chart="props.patientChart"
            :ai-reference-documents="props.aiReferenceDocuments"
            :medical-necessity="props.medicalNecessity"
            :is-editable="props.isEditable"
            :cmm-budgeted="props.cmmBudgeted"
            :order-key="props.orderKey"
            :form-button-clicked="props.formButtonClicked"
            :patient-chart-not-needed="props.patientChartNotNeeded"
            @add-uploaded-file="(change) => emit('addUploadedFile', change)"
            @delete-uploaded-file="(change) => emit('deleteUploadedFile', change)"
            @update-ai-reference-documents="
              (references, removedLinks) => emit('update-ai-reference-documents', references, removedLinks)
            " />
        </v-col>
      </v-row>
    </v-col>

    <v-col v-if="props.isEditable || (props.medicalNecessity && props.medicalNecessity.length)" cols="12">
      <v-row class="section-container">
        <v-col cols="12" class="header-with-select-box">
          <h3 class="text-primary weight-600">Statement of Medical Necessity</h3>
        </v-col>
        <v-col cols="12">
          <v-table class="">
            <thead>
              <tr>
                <th>Uploaded Document</th>
                <th class="text-right"></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Statement of Medical Necessity</td>
                <td>
                  <div class="d-flex align-center justify-end flex-wrap ga-2">
                    <div v-if="medicalNecessity && medicalNecessity.length" class="text-right">
                      <div
                        v-for="link in medicalNecessity"
                        :key="link"
                        class="d-flex align-center justify-end flex-wrap">
                        <span v-if="captionFor(link)" class="text-caption text-medium-emphasis mr-2">
                          {{ captionFor(link) }}
                        </span>
                        <v-btn
                          v-if="props.isEditable"
                          variant="text"
                          color="outline"
                          prepend-icon="mdi-delete"
                          @click="deleteUploadedFile({ link: link, storeVar: `medicalNecessity` })">
                          Delete
                        </v-btn>
                        <v-btn
                          variant="text"
                          color="primary"
                          prepend-icon="mdi-download"
                          @click="openDocument(props.orderKey, link)">
                          Download
                        </v-btn>
                      </div>
                    </div>
                    <div v-else>
                      <div v-if="!props.isEditable" class="">Not Uploaded</div>
                      <div v-else>
                        <ChartUploadFileComponent
                          v-if="props.cmmBudgeted"
                          store-var="medicalNecessity"
                          :other-links="otherLinks"
                          @download-link="addDownloadLink" />
                        <UploadFileComponent
                          v-else
                          :download-destination="`medicalNecessity`"
                          @download-link="addDownloadLink" />
                      </div>
                    </div>
                    <v-btn
                      v-if="canGenerateLetter"
                      variant="text"
                      color="primary"
                      :prepend-icon="letterState.generating ? 'mdi-progress-clock' : 'mdi-file-document-edit-outline'"
                      :disabled="!props.orderKey"
                      @click="onGenerateLetter">
                      {{ letterButtonLabel }}
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-col>
      </v-row>
    </v-col>
    <MedicalNecessityLetterDialog
      v-if="canGenerateLetter"
      ref="letterDialogRef"
      v-model="letterDialogVisible"
      :order-key="props.orderKey"
      :initial-draft="props.medicalNecessityLetter?.draftText"
      @attached="attachGeneratedLetter"
      @state-changed="letterState = $event" />
  </v-row>
</template>

<script setup>
import { computed, ref } from "vue";
import ChartUploadFileComponent from "@/components/ChartUploadFileComponent.vue";
import OrderDocumentList from "@/components/OrderDocumentList.vue";
import UploadFileComponent from "@/components/UploadFileComponent";
import MedicalNecessityLetterDialog from "@/components/MedicalNecessityLetterDialog.vue";
import { usePreparedInfo } from "@/composables/usePreparedInfo";
import { useDocumentLink } from "@/composables/useDocumentLink";
import { useRequirementsStore } from "@/stores/requirementsStore";

const props = defineProps({
  patientChart: {
    type: Array,
    default() {
      return [];
    },
  },
  medicalNecessity: {
    type: Array,
    default() {
      return [];
    },
  },
  medicalNecessityTemplate: {
    type: String,
    default() {
      return "";
    },
  },
  medicalNecessityLetter: {
    type: Object,
    default: null,
  },
  benefitType: {
    type: String,
    default: "",
  },
  isEditable: {
    type: Boolean,
    default() {
      return false;
    },
  },
  aiReferenceDocuments: {
    type: Array,
    default() {
      return [];
    },
  },
  showDocumentList: {
    type: Boolean,
    default: true,
  },
  orderKey: {
    type: String,
    default() {
      return "";
    },
  },
  formButtonClicked: {
    type: Boolean,
    default() {
      return false;
    },
  },
  patientChartNotNeeded: {
    type: Boolean,
    default() {
      return false;
    },
  },
  cmmBudgeted: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const { openDocument } = useDocumentLink();

const otherLinks = computed(() => [
  ...(props.patientChart || []).flatMap((doc) => doc.links?.slice(0, 1) || []),
  ...(props.medicalNecessity || []),
]);
const { remember, captionFor } = usePreparedInfo();
const requirementsStore = useRequirementsStore();

const canGenerateLetter = computed(
  () =>
    props.isEditable &&
    props.benefitType === "medical_benefits" &&
    (requirementsStore.clientConfig?.priorAuth?.medicalNecessityLettersEnabled ?? false)
);

const letterDialogRef = ref(null);
const letterDialogVisible = ref(false);
const letterState = ref({ generating: false, hasDraft: !!props.medicalNecessityLetter?.draftText });

const letterButtonLabel = computed(() => {
  if (letterState.value.generating) return "Drafting letter…";
  return letterState.value.hasDraft ? "Edit generated letter" : "Generate letter";
});

const onGenerateLetter = async () => {
  if (letterState.value.hasDraft || letterState.value.generating) {
    letterDialogVisible.value = true;
    return;
  }
  await letterDialogRef.value?.startDraft();
};

const emit = defineEmits([
  "addUploadedFile",
  "updateGuidelines",
  "deleteUploadedFile",
  "update-ai-reference-documents",
]);

const addDownloadLink = (downloadLinkInfo, chartEntry) => {
  remember(downloadLinkInfo.link, downloadLinkInfo);
  emit("addUploadedFile", chartEntry ? { ...downloadLinkInfo, chartEntry } : downloadLinkInfo);
};

const deleteUploadedFile = (downloadLinkInfo) => {
  downloadLinkInfo.isDelete = true;
  emit("deleteUploadedFile", downloadLinkInfo);
};

const attachGeneratedLetter = ({ link, removedLinks }) => {
  for (const removed of removedLinks) {
    emit("deleteUploadedFile", { link: removed, storeVar: "medicalNecessity", isDelete: true });
  }
  emit("addUploadedFile", { link, storeVar: "medicalNecessity" });
};
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";
</style>
